/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import {
  EntitySegment,
  EntitySegmentConfig,
  EntitySegmentEventSubscription,
  EntitySegmentGroupList,
} from './Entities/entities';
import { SegmentationServiceConfiguration } from './Segmentation.service.config';
import {
  SegmentationCreateResponse,
  SegmentationGroupCreateResponse,
  SegmentationMessageResponse,
} from './Segmentation.service.defs';
import {
  SegmentationServiceCreateEventDto,
  SegmentationServiceCreateGroupDto,
  SegmentationServiceCreateSegmentDto,
  SegmentationServiceDeleteEventDto,
  SegmentationServiceDeleteGroupDto,
  SegmentationServiceDeleteSegmentDto,
  SegmentationServiceGetEventDto,
  SegmentationServiceGetSegmentDto,
  SegmentationServiceSetConfigDto,
  SegmentationServiceUpdateEventDto,
  SegmentationServiceUpdateGroupDto,
  SegmentationServiceUpdateSegmentDto,
} from './Segmentation.service.dto';

/**
 * The segmentation service.
 * @export
 * @class SegmentationService
 */
export class SegmentationService extends BaseService {
  /**
   * Creates an instance of SegmentationService.
   * @param {SegmentationServiceConfiguration} configuration The configuration options to accept.
   * @memberof SegmentationService
   */
  public constructor(configuration: SegmentationServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Segmentation service.
   * @returns {string | null} The base api url for the Segmentation service.
   * @memberof SegmentationService
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`segmentation`);
  }

  /**
   * Creates api url to get an individual segment.
   * @param {string} segmentId The target segment ID.
   * @returns {string | null} The api url for an individual segment.
   * @memberof SegmentationService
   */
  createSegmentUrl(segmentId: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/segments/${segmentId}`;
  }

  /**
   * Creates api url for an individual group.
   * @param {string} groupId The target group ID.
   * @returns {string | null} The api url for an individual group.
   * @memberof SegmentationService
   */
  createGroupUrl(groupId: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/groups/${groupId}`;
  }

  /**
   * Creates api url to get a list of segments.
   * @returns {string} The api url for a list of segments.
   * @memberof SegmentationService
   */
  createSegmentListUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/segments`;
  }

  /**
   * Creates api url to get a list of groups.
   * @returns {string} The api url for a list of groups.
   * @memberof SegmentationService
   */
  createGroupListUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/groups`;
  }

  /**
   * Creates api url to get segment configuration.
   * @returns {string} The api url for segment configuration.
   * @memberof SegmentationService
   */
  createSegmentConfigUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/config`;
  }

  /**
   * Creates api url to get event configuration.
   * @returns {string} The api url for event configuration.
   * @memberof SegmentationService
   */
  createSegmentEventUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/events`;
  }

  /**
   * Attempt to get segment details.
   * @param {SegmentationServiceGetSegmentDto} dto The segment details to fetch.
   * @returns {Observable<EntitySegment | null>} The observable of segment details.
   * @memberof SegmentationService
   */
  getSegment(dto: SegmentationServiceGetSegmentDto): Observable<EntitySegment | null> {
    const { segmentId } = dto;
    const requestUrl = this.createSegmentUrl(segmentId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntitySegment> => {
          return this._handleResponseError<EntitySegment>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of organisation segments.
   * @returns {Observable<Record<string, EntitySegment> | null>} The observable of organisation segments.
   * @memberof SegmentationService
   */
  getSegments(): Observable<Record<string, EntitySegment> | null> {
    const requestUrl = this.createSegmentListUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntitySegment>> => {
          return this._handleResponseError<Record<string, EntitySegment>>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of organisation segment groups.
   * @returns {Observable<EntitySegmentGroupList | null>} The observable of organisation segment groups.
   * @memberof SegmentationService
   */
  getGroups(): Observable<EntitySegmentGroupList | null> {
    const requestUrl = this.createGroupListUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntitySegmentGroupList> => {
          return this._handleResponseError<EntitySegmentGroupList>(value);
        }),
      );
  }

  /**
   * Create group.
   * @param {SegmentationServiceCreateGroupDto} dto The group details to create.
   * @returns {Observable<SegmentationGroupCreateResponse | null>} The observable of group details.
   * @memberof SegmentationService
   */
  createGroup(dto: SegmentationServiceCreateGroupDto): Observable<SegmentationGroupCreateResponse | null> {
    const { group } = dto;
    const requestUrl = this.createGroupListUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: group,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SegmentationGroupCreateResponse> => {
          return this._handleResponseError<SegmentationGroupCreateResponse>(value);
        }),
      );
  }

  /**
   * Update group.
   * @param {SegmentationServiceUpdateGroupDto} dto The group details to update.
   * @returns {Observable<SegmentationMessageResponse | null>} The observable of group details.
   * @memberof SegmentationService
   */
  updateGroup(dto: SegmentationServiceUpdateGroupDto): Observable<SegmentationMessageResponse | null> {
    const { group, groupId } = dto;
    const requestUrl = this.createGroupUrl(groupId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: group,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SegmentationMessageResponse> => {
          return this._handleResponseError<SegmentationMessageResponse>(value);
        }),
      );
  }

  /**
   * Delete group.
   * @param {SegmentationServiceDeleteGroupDto} dto The group details to delete.
   * @returns {Observable<SegmentationMessageResponse | null>} The observable of delete response.
   * @memberof SegmentationService
   */
  deleteGroup(dto: SegmentationServiceDeleteGroupDto): Observable<SegmentationMessageResponse | null> {
    const { groupId } = dto;
    const requestUrl = this.createGroupUrl(groupId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SegmentationMessageResponse> => {
          return this._handleResponseError<SegmentationMessageResponse>(value);
        }),
      );
  }

  /**
   * Create segment.
   * @param {SegmentationServiceCreateSegmentDto} dto The segment details to create.
   * @returns {Observable<SegmentationCreateResponse | null>} The observable of segment details.
   * @memberof SegmentationService
   */
  createSegment(dto: SegmentationServiceCreateSegmentDto): Observable<SegmentationCreateResponse | null> {
    const { segment } = dto;
    const requestUrl = this.createSegmentListUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: segment,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SegmentationCreateResponse> => {
          return this._handleResponseError<SegmentationCreateResponse>(value);
        }),
      );
  }

  /**
   * Update segment.
   * @param {SegmentationServiceUpdateSegmentDto} dto The segment details to update.
   * @returns {Observable<SegmentationMessageResponse | null>} The observable of segment details.
   * @memberof SegmentationService
   */
  updateSegment(dto: SegmentationServiceUpdateSegmentDto): Observable<SegmentationMessageResponse | null> {
    const { segment, segmentId } = dto;
    const requestUrl = this.createSegmentUrl(segmentId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: segment,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SegmentationMessageResponse> => {
          return this._handleResponseError<SegmentationMessageResponse>(value);
        }),
      );
  }

  /**
   * Delete segment.
   * @param {SegmentationServiceDeleteSegmentDto} dto The segment details to delete.
   * @returns {Observable<SegmentationMessageResponse | null>} The observable of segment details.
   * @memberof SegmentationService
   */
  deleteSegment(dto: SegmentationServiceDeleteSegmentDto): Observable<SegmentationMessageResponse | null> {
    const { segmentId } = dto;
    const requestUrl = this.createSegmentUrl(segmentId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<SegmentationMessageResponse> => {
          return this._handleResponseError<SegmentationMessageResponse>(value);
        }),
      );
  }

  /**
   * Get config.
   * @returns {Observable<EntitySegmentConfig | null>} The observable of segmentation service configuration.
   * @memberof SegmentationService
   */
  getConfig(): Observable<EntitySegmentConfig | null> {
    const requestUrl = this.createSegmentConfigUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntitySegmentConfig> => {
          return this._handleResponseError<EntitySegmentConfig>(value);
        }),
      );
  }

  /**
   * Set config.
   * @param {SegmentationServiceSetConfigDto} dto The organisation config details to set for.
   * @returns {Observable<EntitySegmentConfig | null>} The observable of segmentation service configuration.
   * @memberof SegmentationService
   */
  setConfig(dto: SegmentationServiceSetConfigDto): Observable<Partial<EntitySegmentConfig | null>> {
    const { config } = dto;
    const requestUrl = this.createSegmentConfigUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .put<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: config,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntitySegmentConfig> => {
          return this._handleResponseError<EntitySegmentConfig>(value);
        }),
      );
  }

  /**
   * Get a list of event configurations.
   * @returns {Observable<Record<string, EntitySegmentEventSubscription> | null>} The observable of events.
   * @memberof SegmentationService
   */
  getEvents(): Observable<Record<string, EntitySegmentEventSubscription> | null> {
    const requestUrl = this.createSegmentEventUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntitySegmentEventSubscription>> => {
          return this._handleResponseError<Record<string, EntitySegmentEventSubscription>>(value);
        }),
      );
  }

  /**
   * Get a single event by it's subscription id.
   * @param {SegmentationServiceGetEventDto} dto The dto to get the event.
   * @returns {Observable<Record<string, EntitySegmentEventSubscription> | null>} The observable of events.
   * @memberof SegmentationService
   */
  getEvent(dto: SegmentationServiceGetEventDto): Observable<Record<string, EntitySegmentEventSubscription> | null> {
    const { id } = dto;
    const requestUrl = `${this.createSegmentEventUrl()}/${id}`;

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntitySegmentEventSubscription>> => {
          return this._handleResponseError<Record<string, EntitySegmentEventSubscription>>(value);
        }),
      );
  }

  /**
   * Create a new event.
   * @param {SegmentationServiceCreateEventDto} dto The data for the event.
   * @returns {Observable<Record<string, EntitySegmentEventSubscription> | null>} The observable of events.
   * @memberof SegmentationService
   */
  createEvent(
    dto: SegmentationServiceCreateEventDto,
  ): Observable<Record<string, EntitySegmentEventSubscription> | null> {
    const { config } = dto;
    const requestUrl = this.createSegmentEventUrl();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: JSON.stringify(config),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntitySegmentEventSubscription>> => {
          return this._handleResponseError<Record<string, EntitySegmentEventSubscription>>(value);
        }),
      );
  }

  /**
   * Update an event.
   * @param {SegmentationServiceUpdateEventDto} dto The data for the event.
   * @returns {Observable<Record<string, EntitySegmentEventSubscription> | null>} The observable of events.
   * @memberof SegmentationService
   */
  updateEvent(
    dto: SegmentationServiceUpdateEventDto,
  ): Observable<Record<string, EntitySegmentEventSubscription> | null> {
    const { config, id } = dto;
    const requestUrl = `${this.createSegmentEventUrl()}/${id}`;

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: JSON.stringify(config),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntitySegmentEventSubscription>> => {
          return this._handleResponseError<Record<string, EntitySegmentEventSubscription>>(value);
        }),
      );
  }

  /**
   * Delete an event.
   * @param {SegmentationServiceDeleteEventDto} dto The data for the event.
   * @returns {Observable<Record<string, EntitySegmentEventSubscription> | null>} The observable of events.
   * @memberof SegmentationService
   */
  deleteEvent(
    dto: SegmentationServiceDeleteEventDto,
  ): Observable<Record<string, EntitySegmentEventSubscription> | null> {
    const { id } = dto;
    const requestUrl = `${this.createSegmentEventUrl()}/${id}`;

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Record<string, EntitySegmentEventSubscription>> => {
          return this._handleResponseError<Record<string, EntitySegmentEventSubscription>>(value);
        }),
      );
  }
}
